export type ExpressionContext = Record<string, boolean | number | string>;

type Token =
  | { type: 'number'; value: number }
  | { type: 'boolean'; value: boolean }
  | { type: 'identifier'; name: string }
  | { type: 'operator'; value: string }
  | { type: 'lparen' }
  | { type: 'rparen' };

type Ast =
  | { kind: 'number'; value: number }
  | { kind: 'boolean'; value: boolean }
  | { kind: 'identifier'; name: string }
  | { kind: 'unary'; op: string; operand: Ast }
  | { kind: 'binary'; op: string; left: Ast; right: Ast };

const TWO_CHAR_OPERATORS = ['==', '!=', '>=', '<=', '&&', '||'];
const SINGLE_CHAR_OPERATORS = new Set(['+', '-', '*', '/', '!', '<', '>']);

function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < source.length) {
    const ch = source[i];
    if (/\s/.test(ch)) {
      i++;
      continue;
    }
    if (ch >= '0' && ch <= '9') {
      let j = i;
      while (j < source.length && /[0-9.]/.test(source[j])) j++;
      const text = source.slice(i, j);
      const value = Number(text);
      if (Number.isNaN(value)) throw new Error(`Número inválido: "${text}"`);
      tokens.push({ type: 'number', value });
      i = j;
      continue;
    }
    if (/[A-Za-z_]/.test(ch)) {
      let j = i;
      while (j < source.length && /[A-Za-z0-9_]/.test(source[j])) j++;
      const name = source.slice(i, j);
      if (name === 'true' || name === 'false') {
        tokens.push({ type: 'boolean', value: name === 'true' });
      } else {
        tokens.push({ type: 'identifier', name });
      }
      i = j;
      continue;
    }
    const pair = source.slice(i, i + 2);
    if (TWO_CHAR_OPERATORS.includes(pair)) {
      tokens.push({ type: 'operator', value: pair });
      i += 2;
      continue;
    }
    if (SINGLE_CHAR_OPERATORS.has(ch)) {
      tokens.push({ type: 'operator', value: ch });
      i++;
      continue;
    }
    if (ch === '(') {
      tokens.push({ type: 'lparen' });
      i++;
      continue;
    }
    if (ch === ')') {
      tokens.push({ type: 'rparen' });
      i++;
      continue;
    }
    throw new Error(`Caractere inesperado: "${ch}"`);
  }
  return tokens;
}

function parse(source: string): Ast {
  const tokens = tokenize(source);
  let pos = 0;

  const peek = (): Token | undefined => tokens[pos];
  const next = (): Token | undefined => tokens[pos++];

  function parsePrimary(): Ast {
    const token = next();
    if (!token) throw new Error('Expressão incompleta');
    switch (token.type) {
      case 'number':
        return { kind: 'number', value: token.value };
      case 'boolean':
        return { kind: 'boolean', value: token.value };
      case 'identifier':
        return { kind: 'identifier', name: token.name };
      case 'lparen': {
        const expr = parseOr();
        const closing = next();
        if (!closing || closing.type !== 'rparen') throw new Error('Parêntese não fechado');
        return expr;
      }
      default:
        throw new Error(`Token inesperado: "${token.type}"`);
    }
  }

  function parseUnary(): Ast {
    const token = peek();
    if (token && token.type === 'operator' && (token.value === '!' || token.value === '-' || token.value === '+')) {
      next();
      return { kind: 'unary', op: token.value, operand: parseUnary() };
    }
    return parsePrimary();
  }

  function parseMultiplicative(): Ast {
    let left = parseUnary();
    for (;;) {
      const token = peek();
      if (token && token.type === 'operator' && (token.value === '*' || token.value === '/')) {
        next();
        left = { kind: 'binary', op: token.value, left, right: parseUnary() };
      } else {
        return left;
      }
    }
  }

  function parseAdditive(): Ast {
    let left = parseMultiplicative();
    for (;;) {
      const token = peek();
      if (token && token.type === 'operator' && (token.value === '+' || token.value === '-')) {
        next();
        left = { kind: 'binary', op: token.value, left, right: parseMultiplicative() };
      } else {
        return left;
      }
    }
  }

  function parseRelational(): Ast {
    let left = parseAdditive();
    for (;;) {
      const token = peek();
      if (token && token.type === 'operator' && ['>', '>=', '<', '<='].includes(token.value)) {
        next();
        left = { kind: 'binary', op: token.value, left, right: parseAdditive() };
      } else {
        return left;
      }
    }
  }

  function parseEquality(): Ast {
    let left = parseRelational();
    for (;;) {
      const token = peek();
      if (token && token.type === 'operator' && (token.value === '==' || token.value === '!=')) {
        next();
        left = { kind: 'binary', op: token.value, left, right: parseRelational() };
      } else {
        return left;
      }
    }
  }

  function parseAnd(): Ast {
    let left = parseEquality();
    for (;;) {
      const token = peek();
      if (token && token.type === 'operator' && token.value === '&&') {
        next();
        left = { kind: 'binary', op: token.value, left, right: parseEquality() };
      } else {
        return left;
      }
    }
  }

  function parseOr(): Ast {
    let left = parseAnd();
    for (;;) {
      const token = peek();
      if (token && token.type === 'operator' && token.value === '||') {
        next();
        left = { kind: 'binary', op: token.value, left, right: parseAnd() };
      } else {
        return left;
      }
    }
  }

  const ast = parseOr();
  if (pos < tokens.length) throw new Error(`Token inesperado no fim da expressão`);
  return ast;
}

function toNumber(value: boolean | number | string): number {
  if (typeof value === 'boolean') return value ? 1 : 0;
  return typeof value === 'number' ? value : Number(value);
}

function toBoolean(value: boolean | number | string): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  return value.length > 0;
}

function evaluate(ast: Ast, context: ExpressionContext): boolean | number | string {
  switch (ast.kind) {
    case 'number':
      return ast.value;
    case 'boolean':
      return ast.value;
    case 'identifier': {
      if (!Object.prototype.hasOwnProperty.call(context, ast.name)) {
        throw new Error(`Variável desconhecida: "${ast.name}"`);
      }
      return context[ast.name];
    }
    case 'unary': {
      const operand = evaluate(ast.operand, context);
      if (ast.op === '!') return !toBoolean(operand);
      if (typeof operand === 'boolean' || typeof operand === 'string') {
        throw new Error(`Operador "${ast.op}" requer valor numérico`);
      }
      return ast.op === '-' ? -operand : operand;
    }
    case 'binary': {
      const left = evaluate(ast.left, context);
      const right = evaluate(ast.right, context);
      switch (ast.op) {
        case '&&':
          return toBoolean(left) ? right : left;
        case '||':
          return toBoolean(left) ? left : right;
        case '==':
          return toNumber(left) === toNumber(right);
        case '!=':
          return toNumber(left) !== toNumber(right);
        default: {
          if (typeof left === 'string' || typeof right === 'string') {
            throw new Error(`Operador "${ast.op}" requer valores numéricos`);
          }
          if (typeof left === 'boolean' || typeof right === 'boolean') {
            throw new Error(`Operador "${ast.op}" requer valores numéricos`);
          }
          switch (ast.op) {
            case '+':
              return left + right;
            case '-':
              return left - right;
            case '*':
              return left * right;
            case '/':
              return left / right;
            case '>':
              return left > right;
            case '>=':
              return left >= right;
            case '<':
              return left < right;
            case '<=':
              return left <= right;
            default:
              throw new Error(`Operador não suportado: "${ast.op}"`);
          }
        }
      }
    }
  }
}

export function parseExpression(source: string): Ast {
  return parse(source);
}

export function safeEvaluate(source: string, context: ExpressionContext): boolean | number | string {
  return evaluate(parse(source), context);
}